import { OrderDetailAttributes, SubRouteAttributes, WarehouseAttributes } from '@albatrosdeveloper/ave-models-package';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IPaginationResult } from 'app/common/interfaces';
import { DecimalConfigService } from 'app/services/decimal-config.service';
import { ExportDialogComponent, ExportOptions } from 'app/layout/export-overlay';
import { WarehouseService } from 'app/modules/components/configuration/warehouse/warehouse.service';
import { SubRouteService } from 'app/modules/components/routing/sub-routes/sub-route.service';
import { getCode } from 'app/utils/method';
import { debounceTime, distinctUntilChanged, Subscription, skip, take } from 'rxjs';
import { ORDER_SELLER_EXPORT_ID, ORDER_SELLER_TITLE } from '../order-customer/exports/order-customer.constants';
import { OrderCustomerExport } from '../order-customer/exports/order-customer.export';
import { OrderCustomerService } from '../order-customer/order-customer.service';
import { CommonModule, DecimalPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavContainer, MatSidenavModule } from '@angular/material/sidenav';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { CompanyService } from 'app/modules/components/configuration/company/company.service.service';
import { SearchService } from 'app/layout/common/search-page/search-page.service';
import { FilterComponent } from 'app/common/components/filter/filter.component';
import { DecimalFormatPipe } from 'app/pipes/decimal-company.pipe';
import { IFilters } from 'app/modules/components/filters/interfaces/filterInterface';
import { BusinessPartnersFiltersComponent } from 'app/modules/components/filters/business-partners-filters/business-partners-filters.component';
import { ConfigurationFiltersComponent } from 'app/modules/components/filters/configuration-filters/configuration-filters.component';
import { DatesFiltersComponent } from 'app/modules/components/filters/dates-filters/dates-filters.component';
import { DocumentsFiltersComponent } from 'app/modules/components/filters/documents-filters/documents-filters.component';
import { MaterialsManagementFiltersComponent } from 'app/modules/components/filters/materials-management-filters/materials-management-filters.component';
import { RoutesFiltersComponent } from 'app/modules/components/filters/routes-filters/routes-filters.component';
import { StatusFiltersComponent } from 'app/modules/components/filters/status-filters/status-filters.component';
import { FilterSectionComponent } from 'app/common/components/filter-section/filter-section.component';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { MatMenuModule, MatMenuTrigger } from "@angular/material/menu";
import { DateHelper } from 'app/utils/date.helper';
import { CompanyCurrencyPipe } from 'app/pipes/currency.pipe';

// SmartGrid imports
import { SmartGridComponent } from 'app/shared/components/smart-grid/components/smart-grid/smart-grid.component';
import { ColDef } from 'app/shared/components/smart-grid/core/interfaces/col-def.interface';
import { GridConfig } from 'app/shared/components/smart-grid/core/interfaces/grid-config.interface';
import { PageChangedEvent } from 'app/shared/components/smart-grid/core/interfaces/pagination-state.interface';
import { SortChangedEvent } from 'app/shared/components/smart-grid/core/interfaces/sort-state.interface';

// Cell Renderer imports
import { RouteUserCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/route-user-cell-renderer/route-user-cell-renderer.component';
import { SerialNavigationCellRendererComponent } from 'app/modules/components/reports/exchange/request-change/cell-renderers/serial-navigation-cell-renderer.component';
import { DateHourCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/date-hour-cell-renderer/date-hour-cell-renderer.component';
import { CustomerCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/customer-cell-renderer/customer-cell-renderer.component';
import { MaterialCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/material-cell-renderer/material-cell-renderer.component';
import { QuantityCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/quantity-cell-renderer/quantity-cell-renderer.component';
import { DecimalCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/decimal-cell-renderer/decimal-cell-renderer.component';
import { CurrencyCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/currency-cell-renderer/currency-cell-renderer.component';
import { DocumentTypeEnum } from 'app/common/enums/document-types.enum';
import { MatTableDataSource } from '@angular/material/table';
import { UserRouteDisplayComponent } from 'app/shared/components/user-route-display/user-route-display.component';
import { UnitMeasureDisplayComponent } from 'app/shared/components/unit-measure-display/unit-measure-display.component';
import { BusinessPartnerCellRendererComponent } from 'app/modules/components/monitor/monitor-routes/cell-renderers/business-partner-cell-renderer.component';


@Component({
  selector: "app-order-seller",
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavContainer,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    MatDialogModule,
    NgxMatSelectSearchModule,
    FilterComponent,
    DecimalFormatPipe,
    StatusFiltersComponent,
    MaterialsManagementFiltersComponent,
    DatesFiltersComponent,
    ConfigurationFiltersComponent,
    DocumentsFiltersComponent,
    RoutesFiltersComponent,
    BusinessPartnersFiltersComponent,
    FilterSectionComponent,
    UserRouteDisplayComponent,
    UnitMeasureDisplayComponent,
    HoverMenuDirective,
    MatMenuModule,
    CompanyCurrencyPipe,
    SmartGridComponent,
  ],
  providers: [OrderCustomerExport, DecimalPipe],
  templateUrl: "./order-seller.component.html",
  styleUrl: "./order-seller.component.scss",
})
export class OrderSellerComponent implements OnInit, OnDestroy {
  getCode = getCode;
  private isInitialized = false;
  private decimalPipe = new DecimalPipe("en-US");

  // SmartGrid data properties
  data: any[] = [];
  totalItems: number = 0;
  loading: boolean = false;
  searchControl = new FormControl("");
  search: string = "";

  DocumentTypeEnum = DocumentTypeEnum;
  type = DocumentTypeEnum.PEDIDO_DE_VENDEDOR; //Pedido Vendedor

  displayedColumns: string[] = [
    "routeUser",
    "serials",
    "date",
    "shipToExternalKeyCode",
    "materialName",
    "soldQuantity",
    //"baseUnitOfMeasure",
    "unitPrice",
    "subTotal",
    "total",
  ];

  dataSource = new MatTableDataSource<OrderDetailAttributes>([]);

  pageSize: number = 50;
  pageIndex: number = 1;
  sortActive: keyof OrderDetailAttributes = "total";
  sortDirection: "ASC" | "DESC" = "DESC";

  filter = {
    dateini: new Date(),
    dateend: new Date(),
    warehouseId: null,
    routes: [],
  };

  range = new FormGroup({
    start: new FormControl<Date | null>(new Date()),
    end: new FormControl<Date | null>(new Date()),
  });

  warehouseList: WarehouseAttributes[] = [];
  warehouseFilterList: WarehouseAttributes[] = [];
  searchWarehouse = "";

  subRouteList: SubRouteAttributes[] = [];
  listFilterSubRouteList: SubRouteAttributes[] = [];
  searchSubRoute = "";

  UMPActive: boolean = false;
  currencyCode: string = "";

  //BUSCADOR
  searchSubscription: Subscription;

  form: FormGroup;

  //Para filtros
  selectedStatuses: number[] = [];

  // Estados ERP
  selectedERPStatuses: number[] = [];

  selectedCustomerType: string[] = [];

  loadConfigData = false;
  loadDocumentsData = false;
  loadRoutesData = false;
  loadBusinessPartnersData = false;
  loadMaterialsData = false;

  // SmartGrid column definitions
  columnDefs: ColDef<any>[] = [
    {
      colId: "routeUser",
      field: "subRoute.name",
      headerName: "Ruta - Usuario",
      cellRenderer: RouteUserCellRendererComponent,
      sortable: true,
      filterOptions: {
        valueExtractor: (item: any) => item.subRoute?.name,
        customFilterFn(rowData, filterValue) {
          const subRouteName = rowData.subRoute?.name;
          if (!subRouteName) return !filterValue;
          return subRouteName === filterValue;
        },
      },
    },
    {
      field: "serials",
      headerName: "Nro. Doc.",
      cellRenderer: SerialNavigationCellRendererComponent,
      cellRendererParams: {
        documentType: this.type,
      },
      sortable: true,
    },
    {
      field: "date",
      headerName: "Fecha",
      cellRenderer: DateHourCellRendererComponent,
      sortable: true,
      filterOptions: {
        valueExtractor(item) {
          if (!item.date) return "";
          const date = new Date(item.date);
          return date.toLocaleDateString("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
        },
        customFilterFn(rowData, filterValue) {
          const rowDate = rowData.date;
          if (!rowDate) return !filterValue;
          const rowDateObj = new Date(rowDate);
          const rowDateStr = rowDateObj.toLocaleDateString("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
          return rowDateStr === filterValue;
        },
      },
    },
    {
      field: "shipToExternalKeyCode",
      headerName: "Pagador/Receptor",
      cellRenderer: BusinessPartnerCellRendererComponent,
      autoSize: true,
      minWidth: 250, // Much wider for code + long name
      sortable: true,
      filterOptions: {
        // Display customer name in filter dropdown instead of code
        // Note: Requires shipToName field to be present in row data
        valueExtractor: (item: any) => item?.soldTo?.name,
        customFilterFn(rowData, filterValue) {
          const rowCustomer = rowData?.soldTo?.name;
          if (!rowCustomer) return !filterValue;

          return rowCustomer === filterValue;
        },
      },
    },
    {
      field: "materialName",
      headerName: "Material",
      cellRenderer: MaterialCellRendererComponent,
      sortable: true,
      filterOptions: {
        valueExtractor: (item: any) => item.materialName?.name,
        customFilterFn(rowData, filterValue) {
          const rowMaterial = rowData.materialName?.name;
          if (!rowMaterial) return !filterValue;
          return rowMaterial === filterValue;
        },
      },
    },
    {
      field: "soldQuantity",
      headerName: "Cantidad (U.M)",
      cellRenderer: QuantityCellRendererComponent,
      cellRendererParams: {
        getUMPActive: () => this.UMPActive,
      },
      sortable: true,
    },
    {
      field: "unitPrice",
      headerName: "Precio",
      cellRenderer: CurrencyCellRendererComponent,
      sortable: true,
      headerClass: "text-right",
      cellClass: "text-right",
      filterOptions: this.createCurrencyFilterOptions("unitPrice"),
    },
    {
      field: "subTotal",
      headerName: "Subtotal",
      cellRenderer: CurrencyCellRendererComponent,
      sortable: true,
      headerClass: "text-right",
      cellClass: "text-right",
      filterOptions: this.createCurrencyFilterOptions("subTotal"),
    },
    {
      field: "total",
      headerName: "Total",
      cellRenderer: CurrencyCellRendererComponent,
      sortable: true,
      headerClass: "text-right",
      cellClass: "text-right",
      filterOptions: this.createCurrencyFilterOptions("total"),
    },
  ];

  // SmartGrid configuration
  gridConfig: GridConfig<any> = {
    rowIdField: "_id" as any,
    emptyState: {
      message: "No encontramos tus resultados!",
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: "api",
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: "api",
      multiColumn: false,
      defaultSort: [{ field: "total" as any, direction: "desc" }],
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: "local",
    },
    columnVisibility: {
      enabled: false, // Disabled - now in settings panel
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40, // Increased for more spacing
    },
    styling: {
      maxHeight: "85vh",
      minHeight: "80vh",
      density: "comfortable",
      fontSize: "12px",
      cellPaddingX: "3px",
      cellPaddingY: "3px",
    },
    settingsPanel: {
      enabled: true,
      position: "right",
      enableColumnVisibility: true,
      defaultDensity: "compact",
    },
  };

  constructor(
    private orderCustomer: OrderCustomerService,
    private readonly warehouseService: WarehouseService,
    private readonly subRouteService: SubRouteService,
    private readonly companyService: CompanyService,
    private readonly searchService: SearchService,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
    private orderCustomerExport: OrderCustomerExport,
    private companyCurrencyPipe: CompanyCurrencyPipe,
    private decimalConfigService: DecimalConfigService,
    private fb: FormBuilder
  ) {
    const today = new Date();
    this.form = this.fb.group({
      start: [today],
      end: [today],
      startTrip: [],
      endTrip: [],
      companyId: [],
      salesOfficeId: [],
      documentClassId: [],
      subRouteId: [],
      assignedUserId: [],
      payingBusinessPartnerId: [],
      receivingBusinessPartnerId: [],
      warehouseId: [],
      end_startTrip: [],
      end_endTrip: [],
      soldToControl: [],
      searchReceivingBusinessPartner: [],
      searchAssignedUser: [],
      materialGroupId: [[]],
      materialId: [],
    });
  }

  onConfigExpanded(): void {
    this.loadConfigData = true;
  }

  onDocumentsExpanded(): void {
    this.loadDocumentsData = true;
  }

  onRoutesExpanded(): void {
    this.loadRoutesData = true;
  }

  onBusinessPartnersExpanded(): void {
    this.loadBusinessPartnersData = true;
  }

  onMaterialsExpanded(): void {
    this.loadMaterialsData = true;
  }

  ngOnInit(): void {
    this.onHandleSearch();
    this.fetchCompanies();
    this.onFilter();
    this.isInitialized = true;
  }

  ngAfterViewInit(): void {
    this.searchControl.valueChanges
      .pipe(skip(1), debounceTime(500), distinctUntilChanged())
      .subscribe((filterValue: string) => {
        if (this.isInitialized) {
          this.pageIndex = 1;
          this.search = filterValue;
          this.onFilter();
        }
      });
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$
      .pipe(skip(1), debounceTime(300))
      .subscribe((term) => {
        if (this.isInitialized) {
          this.pageIndex = 1;
          this.search = term;
          this.onFilter();
        }
      });
  }

  onFilter() {
    this.range.valueChanges.subscribe((rangeValues) => {
      this.filter.dateini = rangeValues.start || this.filter.dateini;
      this.filter.dateend = rangeValues.end || this.filter.dateend;
    });

    this.getListReport();
  }

  onClearFilter() {
    this.filter = {
      dateini: new Date(),
      dateend: new Date(),
      warehouseId: null,
      routes: [],
    };
    this.range.reset({
      start: new Date(),
      end: new Date(),
    });
    const today = new Date();
    this.form.reset({ start: today, end: today });
    this.onFilter();
  }

  applyFilter(filterValue: string) {
    this.pageIndex = 1;
    this.search = filterValue;
    this.getListReport();
  }

  /**
   * Handle page change events from SmartGrid
   */
  onPageChanged(event: PageChangedEvent): void {
    this.pageIndex = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.getListReport();
  }

  /**
   * Handle sort change events from SmartGrid
   */
  onSortChanged(event: SortChangedEvent<any>): void {
    if (event.sortStates.length > 0) {
      const sortState = event.sortStates[0];
      this.sortActive = sortState.field as keyof OrderDetailAttributes;
      this.sortDirection = sortState.direction === "asc" ? "ASC" : "DESC";
    }
    this.getListReport();
  }

  /**
   * Handle search change events from SmartGrid
   */
  onSearchChanged(searchTerm: string): void {
    this.pageIndex = 1;
    this.search = searchTerm;
    this.getListReport();
  }

  getListReport() {
    let options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.search,
      sort: this.getSortField(),
      sortDirection: this.sortDirection,
    };

    let filter: IFilters = {
      type: this.type,
      startDate: DateHelper.getStartOfDay(this.form.get("start")?.value),
      endDate:
        DateHelper.getEndOfDay(this.form.get("end")?.value) ??
        DateHelper.getEndOfDay(this.form.get("start")?.value) ??
        null,
      startTrip: DateHelper.getStartOfDay(this.form.get("startTrip")?.value),
      endTrip:
        DateHelper.getEndOfDay(this.form.get("endTrip")?.value) ??
        DateHelper.getEndOfDay(this.form.get("startTrip")?.value) ??
        null,
      end_startTrip: DateHelper.getStartOfDay(
        this.form.get("end_startTrip")?.value
      ),
      end_endTrip:
        DateHelper.getEndOfDay(this.form.get("end_endTrip")?.value) ??
        DateHelper.getEndOfDay(this.form.get("end_startTrip")?.value) ??
        null,
      companyId: this.form.get("companyId")?.value,
      salesOfficeId: this.form.get("salesOfficeId")?.value,
      warehouseId: this.form.get("warehouseId")?.value, //
      documentClass: this.form.get("documentClassId")?.value,
      subRouteId: this.form.get("subRouteId")?.value, //
      soldTo: this.form.get("payingBusinessPartnerId")?.value,
      shipTo: this.form.get("receivingBusinessPartnerId")?.value,
      seller: this.form.get("assignedUserId")?.value,
      status: this.selectedStatuses ? this.selectedStatuses : null,
      statusERP: this.selectedERPStatuses.length
        ? this.selectedERPStatuses
        : null,
      typeCustomer: this.selectedCustomerType.length
        ? this.selectedCustomerType
        : null,
      materialGroupId: this.form.get("materialGroupId").value,
      materialId: this.form.get("materialId").value,
      ordersToFilter: null,
    };

    this.loading = true;
    this.orderCustomer.findAllWithCount(options, filter).subscribe({
      next: (res: IPaginationResult<OrderDetailAttributes>) => {
        this.data = res?.data?.[0]?.data ?? [];
        this.totalItems = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        this.loading = false;
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al obtener reporte",
          "Cerrar",
          { duration: 5000 }
        );
        this.loading = false;
      },
    });
  }

  private getSortField(): keyof OrderDetailAttributes {
    const sortFieldMapping: Record<string, keyof OrderDetailAttributes> = {
      subRoute: <any>"subRoute.name",
      user: <any>"user",
      serials: <any>"serials",
      date: <any>"date",
      shipToExternalKeyCode: <any>"shipToExternalKeyCode",
      materialName: <any>"materialName.name",
      soldQuantity: "soldQuantity",
      baseUnitOfMeasure: "baseUnitOfMeasure",
      unitPrice: "unitPrice",
      subTotal: "subTotal",
      total: "total",
    };
    return sortFieldMapping[this.sortActive] || "total";
  }

  /**
   * Format a number as currency with symbol and thousands separators
   * Example: 1250.5 -> "L 1,250.50"
   */
  private formatCurrency(value: number): string {
    if (value == null) return "-";

    const decimals = this.decimalConfigService.getDecimals();
    const formattedNum =
      this.decimalPipe.transform(value, `1.${decimals}-${decimals}`) ?? "-";

    if (formattedNum === "-") return "-";

    const symbol = this.companyCurrencyPipe.transform("symbol");
    return `${symbol} ${formattedNum}`;
  }

  /**
   * Create filterOptions for monetary columns
   * Reusable for unitPrice, subTotal, total
   */
  private createCurrencyFilterOptions(fieldPath: string) {
    return {
      valueExtractor: (item: any) => {
        const value = item[fieldPath];
        return this.formatCurrency(value);
      },
      customFilterFn: (rowData: any, filterValue: string) => {
        const value = rowData[fieldPath];
        const formattedValue = this.formatCurrency(value);
        if (!formattedValue || formattedValue === "-") return !filterValue;
        return formattedValue === filterValue;
      },
    };
  }

  //filter
  private fetchCompanies(): void {
    this.companyService.findToComboBox().subscribe((res) => {
      if (res.data.length === 1) {
        this.UMPActive = res.data[0].UMPActive ?? false;
        this.currencyCode = res.data[0].currencyBase?.code || "";
        this.updatePriceHeaders();
      } else {
        this.UMPActive = false;
        this.currencyCode = "";
      }
    });
  }

  /**
   * Update price column headers with currency code
   * NOTE: Not needed anymore since CurrencyCellRendererComponent shows symbol in cells
   */
  private updatePriceHeaders(): void {
    // Currency symbols are now displayed in cells via CurrencyCellRendererComponent
    // Headers remain simple: "Precio", "Subtotal", "Total"
    // No need to update headers dynamically
  }

  private fetchAllWarehouses(): void {
    this.warehouseService.findToComboBox().subscribe({
      next: (res) => {
        this.warehouseList = res.data;
        this.warehouseFilterList = this.warehouseList.slice();
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error fetching warehouses",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  changueOptionsWarehouse(event) {
    if (!this.warehouseList) {
      return;
    }
    let search = event;
    if (!search) {
      this.warehouseFilterList = this.warehouseList.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    this.warehouseFilterList = this.warehouseList.filter(
      (item) =>
        item.externalKeys?.code.toLowerCase().indexOf(search) > -1 ||
        item.name.toLowerCase().indexOf(search) > -1
    );
  }

  changeWarehouse(event): void {
    this.subRouteList = [];
    this.listFilterSubRouteList = [];
    this.getAllRutas();
  }

  private getAllRutas(): void {
    this.subRouteService
      .findToWarehouse(this.filter.warehouseId ? [this.filter.warehouseId] : [])
      .subscribe({
        next: (res) => {
          this.subRouteList = res.data;
          this.listFilterSubRouteList = this.subRouteList.slice();
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error fetching route",
            "Cerrar",
            { duration: 5000 }
          );
        },
      });
  }

  changueOptionsSubRoute(event) {
    if (!this.subRouteList) {
      return;
    }
    let search = event;
    if (!search) {
      this.listFilterSubRouteList = this.subRouteList.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    this.listFilterSubRouteList = this.subRouteList.filter(
      (item) =>
        item.externalKeys?.code.toLowerCase().indexOf(search) > -1 ||
        item.name.toLowerCase().indexOf(search) > -1
    );
  }

  onExportButtonPressed() {
    if (!this.orderCustomerExport.canStartTask()) return;
    const dialogRef = this.matDialog.open(ExportDialogComponent, {
      data: { title: ORDER_SELLER_TITLE },
    });
    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.orderCustomerExport.export(
          options.format,
          options.range,
          this.totalItems,
          this.pageSize,
          this.pageIndex,
          ORDER_SELLER_EXPORT_ID,
          ORDER_SELLER_TITLE
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
}
